/**
 * SearchResultRow — Compact article card for search results
 */
import React from 'react';
import { Badge } from '../../ui/badge';
import { Button } from '../../ui/button';
import {
  BookmarkPlus,
  BookmarkCheck,
  ExternalLink,
  Loader2,
  FolderPlus,
  Archive,
  BookOpen,
} from 'lucide-react';

export interface SearchArticle {
  pmid: string;
  title: string;
  abstract?: string;
  journal?: string;
  authors?: string;
  pub_date?: string;
  ai_summary?: string;
  design_tags?: string[];
}

interface SearchResultRowProps {
  article: SearchArticle;
  isSaved: boolean;
  isSaving: boolean;
  isSelected: boolean;
  onSelect: () => void;
  onSave: () => void;
  onAddToFolder?: () => void;
  onOpenInLitHub?: () => void;
  onOpenInLitArchive?: () => void;
}

export const SearchResultRow: React.FC<SearchResultRowProps> = ({
  article,
  isSaved,
  isSaving,
  isSelected,
  onSelect,
  onSave,
  onAddToFolder,
  onOpenInLitHub,
  onOpenInLitArchive,
}) => {
  const formattedDate = article.pub_date
    ? new Date(article.pub_date).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
      })
    : null;

  const primaryDesignTag = article.design_tags?.[0];
  const contextLine =
    article.ai_summary ||
    (article.abstract ? `${article.abstract.substring(0, 150)}...` : null);

  return (
    <div
      className={`group p-3 border rounded-lg transition-all cursor-pointer ${
        isSelected
          ? 'border-[hsl(var(--primary))] bg-[hsl(var(--primary))]/5'
          : 'border-[hsl(var(--border))] hover:border-[hsl(var(--primary))]/30 hover:bg-[hsl(var(--muted))]/30'
      }`}
      onClick={onSelect}
      data-testid={`search-result-${article.pmid}`}
    >
      <div className="flex items-start gap-3">
        <div className="flex-1 min-w-0">
          <h3
            className={`responsive-title font-medium text-sm leading-snug line-clamp-2 ${
              isSelected
                ? 'text-[hsl(var(--primary))]'
                : 'text-[hsl(var(--foreground))] group-hover:text-[hsl(var(--primary))]'
            } transition-colors`}
          >
            {article.title}
          </h3>

          <div className="flex items-center gap-2 mt-1 flex-wrap">
            {article.journal && (
              <span className="text-xs text-[hsl(var(--primary))] font-medium truncate max-w-[180px]">
                {article.journal}
              </span>
            )}
            {formattedDate && (
              <span className="text-xs text-[hsl(var(--muted-foreground))]">{formattedDate}</span>
            )}
            {primaryDesignTag && (
              <Badge variant="secondary" className="text-[10px] px-1.5 py-0">
                {primaryDesignTag}
              </Badge>
            )}
            {isSaved && (
              <Badge variant="outline" className="text-[10px] text-emerald-700 border-emerald-200">
                In LitHub
              </Badge>
            )}
          </div>

          {contextLine && (
            <p className="text-xs text-[hsl(var(--muted-foreground))] mt-1.5 line-clamp-1">
              {contextLine}
            </p>
          )}
        </div>

        <div className="flex flex-col items-end gap-1 flex-shrink-0">
          {!isSaved ? (
            <Button
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                onSave();
              }}
              disabled={isSaving}
              className="h-8"
              data-testid={`save-article-${article.pmid}`}
            >
              {isSaving ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <>
                  <BookmarkPlus className="w-4 h-4 mr-1" />
                  <span className="text-xs">Save to LitHub</span>
                </>
              )}
            </Button>
          ) : (
            <>
              <Button
                size="sm"
                variant="ghost"
                className="h-8 px-2 text-emerald-700"
                disabled
                data-testid={`saved-badge-${article.pmid}`}
              >
                <BookmarkCheck className="w-4 h-4 mr-1" />
                <span className="text-xs">Saved</span>
              </Button>
              {onOpenInLitHub && (
                <Button
                  size="sm"
                  variant="outline"
                  className="h-7 text-xs"
                  onClick={(e) => {
                    e.stopPropagation();
                    onOpenInLitHub();
                  }}
                >
                  <BookOpen className="w-3 h-3 mr-1" />
                  LitHub
                </Button>
              )}
              {onAddToFolder && (
                <Button
                  size="sm"
                  variant="outline"
                  className="h-7 text-xs"
                  onClick={(e) => {
                    e.stopPropagation();
                    onAddToFolder();
                  }}
                >
                  <FolderPlus className="w-3 h-3 mr-1" />
                  Folder
                </Button>
              )}
              {onOpenInLitArchive && (
                <Button
                  size="sm"
                  variant="outline"
                  className="h-7 text-xs"
                  onClick={(e) => {
                    e.stopPropagation();
                    onOpenInLitArchive();
                  }}
                >
                  <Archive className="w-3 h-3 mr-1" />
                  LitArchive
                </Button>
              )}
            </>
          )}

          <Button
            size="sm"
            variant="ghost"
            className="h-8 w-8 p-0"
            onClick={(e) => {
              e.stopPropagation();
              window.open(`https://pubmed.ncbi.nlm.nih.gov/${article.pmid}/`, '_blank');
            }}
            title="Open in PubMed"
            data-testid={`open-pubmed-${article.pmid}`}
          >
            <ExternalLink className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </div>
  );
};

export default SearchResultRow;
